import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Statistic } from 'antd';
import { ShoppingCartOutlined, InboxOutlined, ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';

const COLORS = {
  success: '#52C41A',
  warning: '#FAAD14',
  danger: '#FF4D4F',
  brand: '#00A651',
  blue: '#1890ff',
};

/**
 * Get shortage rate threshold color
 * > 10% = red, > 5% = yellow, otherwise brand green
 */
const getShortageColor = (rate) => {
  if (rate > 10) return COLORS.danger;
  if (rate > 5) return COLORS.warning;
  return COLORS.brand;
};

/**
 * Module 1: Executive Summary - KPI cards
 * Top-level overview for nationwide operations
 */
const ExecutiveSummary = ({ data, realtimeData }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const timeoutOrders = realtimeData?.timeoutOrders || 0;
  const shortageColor = getShortageColor(data.orderShortageRate);

  const cardStyle = { flex: 1, minWidth: 200 };
  const cardBodyStyle = { padding: '16px 20px' };

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {/* 1. Today's Total Orders */}
      <Card style={cardStyle} styles={{ body: cardBodyStyle }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <ShoppingCartOutlined style={{ fontSize: 18, color: COLORS.blue }} />
          <span style={{ fontSize: 14, color: '#999' }}>{t('dashboard.nationwide.todayTotalOrders')}</span>
        </div>
        <div style={{ fontSize: 32, fontWeight: 'bold', color: COLORS.blue }}>
          {data.todayTotalOrders?.toLocaleString()}
        </div>
      </Card>

      {/* 2. Order Shortage Rate */}
      <Card
        style={{
          ...cardStyle,
          border: data.orderShortageRate > 10 ? `2px solid ${COLORS.danger}` : undefined,
          background: data.orderShortageRate > 10 ? '#fff2f0' : undefined,
        }}
        styles={{ body: cardBodyStyle }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <InboxOutlined style={{ fontSize: 18, color: shortageColor }} />
          <span style={{ fontSize: 14, color: '#999' }}>{t('dashboard.nationwide.orderShortageRate')}</span>
        </div>
        <div style={{ fontSize: 32, fontWeight: 'bold', color: shortageColor }}>
          {data.orderShortageRate}%
        </div>
      </Card>

      {/* 3. Avg Pick & Sort Time */}
      <Card style={cardStyle} styles={{ body: cardBodyStyle }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <ClockCircleOutlined style={{ fontSize: 18, color: '#333' }} />
          <span style={{ fontSize: 14, color: '#999' }}>{t('dashboard.nationwide.avgPickSortTime')}</span>
        </div>
        <div style={{ fontSize: 32, fontWeight: 'bold', color: '#333' }}>
          {data.avgPickingAndSortingTime} <span style={{ fontSize: 16, fontWeight: 400, color: '#999' }}>min</span>
        </div>
      </Card>

      {/* 4. Timeout Orders (SLA) */}
      <Card
        style={{
          ...cardStyle,
          border: '2px solid ' + COLORS.danger,
          background: '#fff2f0',
        }}
        styles={{ body: cardBodyStyle }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <WarningOutlined style={{ fontSize: 18, color: COLORS.danger }} />
          <span style={{ fontSize: 14, color: '#999' }}>{t('dashboard.nationwide.timeoutOrdersSLA')}</span>
        </div>
        <div style={{ fontSize: 32, fontWeight: 'bold', color: COLORS.danger }}>
          {timeoutOrders}
        </div>
      </Card>
    </div>
  );
};

export default ExecutiveSummary;
